import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, MapPin, Phone, Mail, MoreVertical, Trash2, Edit } from 'lucide-react';
import LocationForm from '../components/LocationForm';
import { Location } from '../types/locations';
import { locationsApi } from '../services/locationsApi';
import { isDeviceOnline } from '../types/devices';

const Locations: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const data = await locationsApi.getAllLocations();
      setLocations(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch locations');
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleSave = async (savedLocation: Location) => {
    await fetchLocations();
    setShowForm(false);
    setSelectedLocation(null);
  };

  const handleEdit = (location: Location) => {
    setSelectedLocation(location);
    setShowForm(true);
    setActiveDropdown(null);
  };

  const handleDeleteClick = (location: Location) => {
    setLocationToDelete(location);
    setShowDeleteDialog(true);
    setActiveDropdown(null);
  };

  const handleDeleteConfirm = async () => {
    if (!locationToDelete?.locationId) return;

    try {
      setDeleting(true);
      await locationsApi.deleteLocation(locationToDelete.locationId);
      await fetchLocations(); // Refresh the list
      setShowDeleteDialog(false);
      setLocationToDelete(null);
    } catch (err) {
      setError('Failed to delete location');
      console.error('Error deleting location:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDropdownToggle = (locationId: number) => {
    setActiveDropdown(activeDropdown === locationId ? null : locationId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Locations</h1>
          <p className="text-gray-600">Manage your display locations</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchLocations}
            className="flex items-center px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button 
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 mb-6 text-red-700 bg-red-100 rounded-lg">
          {error}
        </div>
      )}

      {/* Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            Loading locations...
          </div>
        ) : locations.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            No locations found
          </div>
        ) : (
          locations.map((location) => (
            <div key={location.locationId} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{location.locationName}</h3>
                    <p className="text-sm text-gray-500">{location.locationType?.typeName || 'Unassigned'}</p>
                  </div>
                  <div className="relative">
                    <button 
                      className="text-gray-400 hover:text-gray-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDropdownToggle(location.locationId!);
                      }}
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {activeDropdown === location.locationId && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                        <div className="py-1">
                          <button
                            onClick={() => handleEdit(location)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Edit className="w-4 h-4 mr-3" />
                            Edit Location
                          </button>
                          <button
                            onClick={() => handleDeleteClick(location)}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-3" />
                            Delete Location
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-start text-sm">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 mr-2" />
                    <div>
                      <p className="text-gray-600">{location.address}</p>
                      <p className="text-gray-600">{location.city}, {location.postCode}</p>
                    </div>
                  </div>

                  <div className="flex items-center text-sm">
                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                    <p className="text-gray-600">{location.contactPhone}</p>
                  </div>

                  <div className="flex items-center text-sm">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    <p className="text-gray-600">{location.contactPerson}</p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Devices</span>
                    <div className="text-sm">
                      <span className="text-green-600">
                        {location.devices?.filter(d => isDeviceOnline(d.onlineStatus)).length || 0} online
                      </span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="text-gray-600">
                        {location.devices?.length || 0} total
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Location Form */}
      <LocationForm 
        isOpen={showForm}
        location={selectedLocation}
        onClose={() => {
          setShowForm(false);
          setSelectedLocation(null);
        }}
        onSave={handleSave}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && locationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Location
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{locationToDelete.locationName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setLocationToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Locations;